/**
 * PWA Install Prompt composable
 * Singleton composable managing the PWA install prompt flow.
 * Handles beforeinstallprompt capture, platform detection, and session persistence.
 */
import { ref, readonly } from 'vue'

const SESSION_KEY = 'gofood_install_dismissed'

// Shared singleton state
const isVisible = ref(false)
const platform = ref('unsupported') // 'android' | 'ios' | 'desktop-chromium' | 'unsupported'
const isInstalled = ref(false)

let deferredPrompt = null
let pendingShow = false

/**
 * Detect if app is running in standalone/installed mode
 */
function checkIsInstalled() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.matchMedia('(display-mode: fullscreen)').matches ||
    navigator.standalone === true
  )
}

/**
 * Detect platform category
 */
function detectPlatform() {
  const ua = navigator.userAgent || ''
  // iOS detection (including iPadOS via maxTouchPoints)
  if (/iPhone|iPad|iPod/.test(ua) || (navigator.maxTouchPoints > 1 && /Macintosh/.test(ua))) {
    return 'ios'
  }
  // Android/Desktop Chromium will be confirmed when beforeinstallprompt fires
  return 'unsupported'
}

/**
 * Initialize window event listeners. Call from App.vue onMounted.
 */
function initListener() {
  if (checkIsInstalled()) {
    isInstalled.value = true
    return
  }

  platform.value = detectPlatform()

  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault()
    deferredPrompt = e

    // Determine platform based on UA
    const ua = navigator.userAgent || ''
    if (/Android/.test(ua)) {
      platform.value = 'android'
    } else {
      platform.value = 'desktop-chromium'
    }

    // If showIfEligible was already called but we didn't have the prompt yet
    if (pendingShow) {
      pendingShow = false
      show()
    }
  })

  window.addEventListener('appinstalled', () => {
    isInstalled.value = true
    isVisible.value = false
    deferredPrompt = null
  })
}

/**
 * Show the install prompt if eligible. Call from HomeView after delay.
 */
function showIfEligible() {
  // Already installed or already dismissed this session
  if (isInstalled.value) return
  if (sessionStorage.getItem(SESSION_KEY)) return
  if (isVisible.value) return

  const plat = platform.value

  if (plat === 'ios') {
    show()
  } else if (plat === 'android' || plat === 'desktop-chromium') {
    show()
  } else {
    // beforeinstallprompt hasn't fired yet — set pending flag
    pendingShow = true
  }
}

function show() {
  if (sessionStorage.getItem(SESSION_KEY)) return
  isVisible.value = true
}

/**
 * Dismiss the prompt and set session flag
 */
function dismiss() {
  isVisible.value = false
  sessionStorage.setItem(SESSION_KEY, '1')
  pendingShow = false
}

/**
 * Trigger native install prompt (Android/Desktop Chromium only)
 */
async function triggerInstall() {
  if (!deferredPrompt) return

  deferredPrompt.prompt()
  const { outcome } = await deferredPrompt.userChoice

  if (outcome === 'accepted') {
    isInstalled.value = true
  }

  deferredPrompt = null
  isVisible.value = false
  sessionStorage.setItem(SESSION_KEY, '1')
}

export function useInstallPrompt() {
  return {
    initListener,
    showIfEligible,
    dismiss,
    triggerInstall,
    isVisible: readonly(isVisible),
    platform: readonly(platform),
    isInstalled: readonly(isInstalled),
  }
}
